/**
 * Certifications and skills: what people type, and what we can query.
 *
 * Both fields have to be two things at once. A candidate has to be able to
 * enter "Certificación en la Elaboración de Reportes de Sostenibilidad Bajo los
 * Estándares GRI", because that is what their diploma says and a closed list
 * would have made them pick something else or nothing. And a recruiter has to
 * be able to ask "who holds GRI?" and get that person back.
 *
 * So nothing here rewrites what was typed. The raw entries stay exactly as
 * entered, and this module derives a parallel list of canonical ids next to
 * them — the `*_canon` columns. Raw is what the human said; canon is what the
 * database can join on. When they disagree, raw wins, because it is the
 * evidence and canon is only an index of it.
 *
 * ── What the live data actually looks like ──────────────────────────
 *
 * 72 of 300 candidates have entered certifications: 68 distinct strings. The
 * curated suggestions carry most of the weight — `ISO 14001` ×24,
 * `GRI Standards` ×18, `ISO 45001` ×8 — and everything the design has to
 * survive is in the tail:
 *
 *   "Iso 9001" / "ISO 9001"            case, and nothing else
 *   "ISO 14000", "DC3 ISO140001"       a family name and a typo for 14001
 *   "GRI, ISO 14064, GHG Protocol"     three credentials in one entry
 *   "GHG Protocol", "PCAF", "NIIF S1 y S2"   real, and not in any list we wrote
 *   "Diplomado en Derecho Ambiental…"  a course, not a credential
 *   "cvfrvgtfv"                        someone testing the form
 *
 * Three of those shape the code below.
 *
 * `GRI, ISO 14064, GHG Protocol` is why normalization is not a lookup. One
 * entry can name several credentials, so `normalizeEntry` returns a *set* of
 * ids, and the column-level functions union across entries.
 *
 * It is also why we scan rather than split. Splitting on separators looks
 * obvious until the separator is Spanish: "Prevención de riesgos laborales en
 * actividades de alto riesgo: construcción y obra civil" splits on "y" into two
 * fragments that mean nothing. Scanning for known aliases inside the entry
 * finds the three credentials in the GRI example and finds nothing in the
 * prose, which is the correct answer to both.
 *
 * `cvfrvgtfv` is why an unmatched entry is not an error. It normalizes to no
 * ids, keeps its raw text, and is simply not reachable by a canonical query.
 * That is also what happens to the Derecho Ambiental diploma — a real
 * qualification we cannot match on, which is a fact about our vocabulary, not
 * about the candidate.
 *
 * ── Extending it ────────────────────────────────────────────────────
 *
 * Add an entry to the catalog. The ISO rule below already covers the whole ISO
 * family generically, including numbers nobody has written down here, so ISO
 * standards do not need entries at all.
 *
 * Ids are stable: they are stored in the `*_canon` columns of every row, so
 * renaming one silently breaks matching on already-saved data. Labels are free
 * to change.
 */

export interface CredentialEntry {
    /**
     * Stored in the `*_canon` columns. Changing one orphans every row that
     * already carries it — add an alias instead.
     */
    id: string;
    label: string;
    /**
     * Matched as whole tokens anywhere inside an entry, so they find the three
     * credentials in "GRI, ISO 14064, GHG Protocol". Token-bounded rather than
     * a plain substring: "edge" inside "knowledge" is not a certification.
     *
     * The label is scanned too, automatically — do not repeat it here.
     */
    aliases?: string[];
    /**
     * Matched only when they are the *entire* entry. For abbreviations short or
     * common enough that finding them mid-sentence would be a false positive:
     * "well", "cem", "sites", "edge". Someone whose only certification is
     * literally "EDGE" means the IFC building standard; the same four letters
     * inside a sentence do not.
     */
    exactAliases?: string[];
    /** Put the label in `exactAliases` instead of `aliases`. */
    exactOnly?: boolean;
    /**
     * Ids this one also satisfies, so a vacancy asking for `gri` is answered by
     * a candidate holding the GRI professional certification. Expanded one level
     * — deep chains would be a hierarchy, and this is a vocabulary.
     */
    implies?: string[];
}

/**
 * Case, accents and punctuation carry no meaning here — "Iso 9001", "ISO-9001"
 * and "iso 9001" are one credential typed three ways, and all three are live.
 * Folding to lowercase alphanumerics separated by single spaces makes them one
 * string, and makes token-boundary matching a plain space test.
 *
 * Digits survive on purpose: they are the entire content of an ISO number.
 */
export function fold(text: string): string {
    return text
        .normalize("NFD")
        // The combining marks NFD just split off. Escaped rather than written
        // literally: the literal characters are invisible in an editor, and a
        // stray one in this range would be impossible to see.
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

function escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * ISO standards, generically.
 *
 * Nine distinct ISO numbers are already live across candidates and vacancies
 * and only three were ever in the suggestion list, so enumerating them was
 * always going to lose. The number *is* the identity of the standard, so we
 * read it directly and build `iso_<number>` from it.
 *
 * `\s*` rather than `\s+` because "ISO140001" is written without the space, and
 * `{4,6}` because the typo has six digits. Folding has already turned
 * "ISO 14067:2018" into "iso 14067 2018", so the year is a separate token and
 * cannot be read as the standard.
 */
const ISO_PATTERN = /\biso\s*(\d{4,6})/g;

/**
 * Numbers that name something adjacent to the standard people mean.
 *
 * "ISO 14000" is the name of the *family*; the certifiable standard in it is
 * 14001, which is what someone listing it under certifications holds.
 * "ISO 140001" does not exist — it is 14001 with a finger slip, and it is in
 * production once as "DC3 ISO140001".
 */
const ISO_NUMBER_REPAIRS: Record<string, string> = {
    "14000": "14001",
    "140001": "14001",
    "9000": "9001"
};

/** Names for the ISO numbers worth naming. Anything else prints as "ISO n". */
const ISO_LABELS: Record<string, string> = {
    "9001": "ISO 9001 · Calidad",
    "14001": "ISO 14001 · Gestión ambiental",
    "14006": "ISO 14006 · Ecodiseño",
    "14040": "ISO 14040 · Análisis de ciclo de vida",
    "14046": "ISO 14046 · Huella de agua",
    "14064": "ISO 14064 · Gases de efecto invernadero",
    "14067": "ISO 14067 · Huella de carbono de producto",
    "20121": "ISO 20121 · Eventos sostenibles",
    "26000": "ISO 26000 · Responsabilidad social",
    "37001": "ISO 37001 · Antisoborno",
    "37101": "ISO 37101 · Desarrollo sostenible en comunidades",
    "45001": "ISO 45001 · Seguridad y salud laboral",
    "50001": "ISO 50001 · Gestión de la energía"
};

export const CERTIFICATIONS: CredentialEntry[] = [
    // ── Reporting frameworks ────────────────────────────────────────
    {
        id: "gri",
        label: "GRI Standards",
        aliases: ["gri", "global reporting initiative", "estandares gri", "estandar gri", "gri g4"]
    },
    {
        id: "gri_csp",
        label: "GRI Certified Sustainability Professional",
        aliases: ["gri certified sustainability professional", "gri csp"],
        implies: ["gri"]
    },
    {
        id: "sasb",
        label: "SASB (FSA Credential)",
        aliases: ["sasb", "fsa credential", "value reporting foundation"]
    },
    {
        // Live as "NIIF S1 y S2" and "Gri, Niif s1/s2" — the Spanish-language
        // name for IFRS S1/S2, which is what the ISSB actually issues.
        id: "issb",
        label: "ISSB · NIIF/IFRS S1–S2",
        aliases: ["issb", "niif s1", "niif s2", "ifrs s1", "ifrs s2", "niif s1 y s2", "niif s1 s2", "ifrs s1 s2"]
    },
    { id: "tcfd", label: "TCFD" },
    { id: "tnfd", label: "TNFD" },
    { id: "csrd", label: "CSRD / ESRS", aliases: ["csrd", "esrs"] },
    { id: "eu_taxonomy", label: "Taxonomía UE", aliases: ["eu taxonomy", "taxonomia ue", "taxonomia europea"] },
    { id: "integrated_reporting", label: "Integrated Reporting <IR>", aliases: ["integrated reporting", "reporte integrado"] },

    // ── Carbon and climate ──────────────────────────────────────────
    {
        id: "ghg_protocol",
        label: "GHG Protocol",
        aliases: ["ghg protocol", "protocolo ghg", "greenhouse gas protocol", "protocolo de gases de efecto invernadero"]
    },
    { id: "sbti", label: "Science Based Targets (SBTi)", aliases: ["sbti", "science based targets"] },
    { id: "cdp", label: "CDP", aliases: ["cdp", "carbon disclosure project"] },
    { id: "pcaf", label: "PCAF", aliases: ["pcaf", "partnership for carbon accounting financials"] },
    { id: "verra", label: "Verra (VCS)", aliases: ["verra", "vcs", "verified carbon standard"] },
    { id: "gold_standard", label: "Gold Standard" },
    { id: "carbon_trust", label: "Carbon Trust Standard", aliases: ["carbon trust"] },
    { id: "cem", label: "Certified Energy Manager (AEE)", aliases: ["certified energy manager"], exactAliases: ["cem"] },

    // ── Buildings ───────────────────────────────────────────────────
    {
        // "LEED AP" and "LEED (USGBC)" are both live. The accreditation is a
        // stronger claim than familiarity, but both are LEED for matching.
        id: "leed",
        label: "LEED (USGBC)",
        aliases: ["leed", "leed ap", "leed green associate", "usgbc"]
    },
    { id: "breeam", label: "BREEAM" },
    {
        id: "well",
        label: "WELL Building Standard",
        aliases: ["well building standard", "well ap"],
        exactAliases: ["well"]
    },
    { id: "edge_building", label: "EDGE (IFC)", exactOnly: true, exactAliases: ["edge", "edge ifc"] },
    { id: "sites", label: "SITES", exactOnly: true },
    { id: "green_globes", label: "Green Globes" },
    { id: "c2c", label: "Cradle to Cradle", aliases: ["cradle to cradle"], exactAliases: ["c2c"] },

    // ── Corporate and supply chain ──────────────────────────────────
    { id: "b_corp", label: "B Corp", aliases: ["b corp", "bcorp", "b corporation", "sistema b"] },
    { id: "ecovadis", label: "Ecovadis" },
    { id: "gresb", label: "GRESB" },
    { id: "fsc", label: "FSC (Forest Stewardship Council)", aliases: ["fsc", "forest stewardship council"] },
    { id: "rainforest_alliance", label: "Rainforest Alliance" },
    { id: "iscc", label: "ISCC" },
    { id: "sa8000", label: "SA8000" },
    { id: "fair_trade", label: "Fair Trade", aliases: ["fair trade", "fairtrade", "comercio justo"] },
    { id: "haccp", label: "HACCP" },

    // ── Finance ─────────────────────────────────────────────────────
    { id: "cfa_esg", label: "CFA ESG Investing", aliases: ["cfa esg", "cfa certificate in esg investing"] },
    // "pri" is scannable rather than exact — live data has "PRI sustainable
    // investment". Token boundaries already make it safe: "principios" folds to
    // a single token, so "pri" cannot match inside it.
    { id: "pri", label: "PRI · Principles for Responsible Investment", aliases: ["pri", "principles for responsible investment"] },

    // ── General professional ────────────────────────────────────────
    { id: "pmp", label: "PMP (Project Management)", aliases: ["pmp", "project management professional"] }
];

/**
 * Skills.
 *
 * The 25 ids of the closed `jobs.skills` enum are kept exactly as they are, so
 * the 114 vacancies that already carry them normalize to themselves and the
 * scraper prompt keeps working unchanged. What is new is that the columns no
 * longer *only* accept these — anything typed is kept, and these are simply the
 * part we can query on.
 *
 * The eight ids that also appear in `CERTIFICATIONS` — gri, sasb, tcfd, csrd,
 * eu_taxonomy and the ISO numbers — are shared deliberately. Knowing GRI and
 * being certified in GRI are different claims, but they are claims about the
 * same thing, and one id lets a query cross between them.
 */
export const SKILLS: CredentialEntry[] = [
    { id: "analisis_datos", label: "Análisis de Datos", aliases: ["analisis de datos", "analisis datos", "data analysis", "analitica de datos"] },
    {
        id: "reporting_esg",
        label: "Reporting ESG",
        aliases: ["reporting esg", "esg reporting", "reportes esg", "informes esg", "reporte de sostenibilidad", "informe de sostenibilidad", "memoria de sostenibilidad"]
    },
    { id: "gri", label: "GRI", aliases: ["gri", "global reporting initiative"] },
    { id: "sasb", label: "SASB" },
    { id: "tcfd", label: "TCFD" },
    { id: "csrd", label: "CSRD", aliases: ["csrd", "esrs"] },
    {
        // `taxonomia_eu` is the id the closed `jobs.skills` enum used, and 3
        // live vacancies carry it. The canonical id is `eu_taxonomy` so it
        // matches the certification of the same name — without this alias the
        // backfill would read those three rows as naming nothing.
        id: "eu_taxonomy",
        label: "Taxonomía UE",
        aliases: ["eu taxonomy", "taxonomia ue", "taxonomia eu", "taxonomia europea"]
    },
    {
        id: "huella_carbono",
        label: "Cálculo Huella de Carbono",
        aliases: ["huella de carbono", "huella carbono", "carbon footprint", "calculo de huella de carbono", "inventario de gei"]
    },
    {
        id: "ciclo_vida",
        label: "Análisis de Ciclo de Vida",
        aliases: ["analisis de ciclo de vida", "life cycle assessment"],
        exactAliases: ["acv", "lca"]
    },
    { id: "gestion_proyectos", label: "Gestión de Proyectos", aliases: ["gestion de proyectos", "gestion de proyecto", "project management"] },
    {
        id: "stakeholders",
        label: "Gestión de Stakeholders",
        aliases: ["stakeholders", "stakeholder engagement", "grupos de interes", "relacionamiento comunitario", "relaciones comunitarias"]
    },
    { id: "regulacion_ambiental", label: "Regulación Ambiental", aliases: ["regulacion ambiental", "normativa ambiental", "legislacion ambiental", "derecho ambiental"] },
    { id: "excel", label: "Excel", aliases: ["excel", "microsoft excel"] },
    { id: "python", label: "Python" },
    { id: "power_bi", label: "Power BI", aliases: ["power bi", "powerbi"] },
    { id: "comunicacion", label: "Comunicación", aliases: ["comunicacion", "communication", "comunicacion corporativa"] },
    { id: "ingles", label: "Inglés", aliases: ["ingles", "english"] },
    { id: "economia_circular", label: "Economía Circular", aliases: ["economia circular", "circular economy"] },
    { id: "auditoria", label: "Auditoría", aliases: ["auditoria", "auditorias", "auditor", "audit"] },
    { id: "finanzas", label: "Finanzas", aliases: ["finanzas", "finance"] },
    {
        id: "gestion_ambiental",
        label: "Gestión Ambiental",
        aliases: ["gestion ambiental", "environmental management", "sistema de gestion ambiental", "sistemas de gestion ambiental"],
        exactAliases: ["sga"]
    },
    { id: "due_diligence", label: "Due Diligence", aliases: ["due diligence", "debida diligencia"] },
    // Live as "qgis" in a candidate's certifications, which is where someone
    // puts a tool when the form has nowhere else for it. Now it has somewhere.
    { id: "gis", label: "SIG / GIS", aliases: ["qgis", "arcgis", "sistemas de informacion geografica"], exactAliases: ["gis", "sig"] }
];

interface CompiledEntry {
    id: string;
    implies: string[];
    exact: Set<string>;
    scan: RegExp[];
}

function compile(entries: CredentialEntry[]): CompiledEntry[] {
    return entries.map(entry => {
        const scannable = entry.exactOnly ? [] : [entry.label, ...(entry.aliases ?? [])];
        const exact = [...(entry.exactAliases ?? []), ...(entry.exactOnly ? [entry.label] : [])];
        return {
            id: entry.id,
            implies: entry.implies ?? [],
            // The id itself is always an exact alias, so a canonical id fed back
            // in normalizes to itself. That is not hypothetical: `jobs.skills`
            // holds 114 rows written in the closed enum's ids, and backfilling
            // the new columns from it means normalizing the literal string
            // "gestion_proyectos" — which folds to "gestion proyectos" and
            // matches no alias anyone would think to write down.
            //
            // Exact rather than scannable, deliberately. It costs nothing here
            // and it keeps short ids like `gis` and `pri` from becoming
            // substring hazards by the back door.
            exact: new Set([entry.id, ...exact].map(fold).filter(Boolean)),
            // Token-bounded rather than substring: folding leaves single spaces
            // between tokens, so `(^| )alias( |$)` is the whole test.
            scan: [...new Set(scannable.map(fold).filter(Boolean))]
                .map(alias => new RegExp(`(?:^| )${escapeRegExp(alias)}(?:$| )`))
        };
    });
}

const COMPILED_CERTIFICATIONS = compile(CERTIFICATIONS);
const COMPILED_SKILLS = compile(SKILLS);

const CERTIFICATION_LABELS = new Map(CERTIFICATIONS.map(entry => [entry.id, entry.label]));
const SKILL_LABELS = new Map(SKILLS.map(entry => [entry.id, entry.label]));

function scan(raw: string, compiled: CompiledEntry[]): string[] {
    const folded = fold(raw);
    if (!folded) return [];

    const found = new Set<string>();

    for (const entry of compiled) {
        const hit = entry.exact.has(folded) || entry.scan.some(pattern => pattern.test(folded));
        if (!hit) continue;
        found.add(entry.id);
        for (const implied of entry.implies) found.add(implied);
    }

    // The ISO rule runs over the same folded text and is additive: an entry
    // reading "ISO 14001, HACCP" yields both, and neither rule can hide the
    // other's answer.
    ISO_PATTERN.lastIndex = 0;
    for (const match of folded.matchAll(ISO_PATTERN)) {
        const number = ISO_NUMBER_REPAIRS[match[1]] ?? match[1];
        found.add(`iso_${number}`);
    }

    return [...found];
}

/** Canonical ids named by one raw entry. Empty when we recognise nothing. */
export function normalizeCertificationEntry(raw: string): string[] {
    return scan(raw, COMPILED_CERTIFICATIONS);
}

/** Canonical ids named by one raw entry. Empty when we recognise nothing. */
export function normalizeSkillEntry(raw: string): string[] {
    return scan(raw, COMPILED_SKILLS);
}

function normalizeAll(values: unknown, one: (raw: string) => string[]): string[] {
    if (!Array.isArray(values)) return [];
    const found = new Set<string>();
    for (const value of values) {
        if (typeof value !== "string") continue;
        for (const id of one(value)) found.add(id);
    }
    // Sorted so the stored array is stable: an unsorted set would make every
    // re-save look like a change to the history table.
    return [...found].sort();
}

/** What goes in a `*_certifications_canon` column, given the raw entries. */
export function normalizeCertifications(values: unknown): string[] {
    return normalizeAll(values, normalizeCertificationEntry);
}

/** What goes in a `*_skills_canon` column, given the raw entries. */
export function normalizeSkills(values: unknown): string[] {
    return normalizeAll(values, normalizeSkillEntry);
}

/** Printable name for a canonical id, including ones the ISO rule invented. */
export function credentialLabel(id: string): string {
    const iso = /^iso_(\d{4,6})$/.exec(id);
    if (iso) return ISO_LABELS[iso[1]] ?? `ISO ${iso[1]}`;
    return CERTIFICATION_LABELS.get(id) ?? SKILL_LABELS.get(id) ?? id;
}

/**
 * What the forms offer.
 *
 * The public questionnaire and the vacancy form both autocomplete against these
 * and both accept anything not on them. Built from the catalog rather than
 * hand-listed on each side, because the previous hand-listed copy in
 * `QuestionnaireWizard.tsx` is exactly what drifted: it offered "ISO 26000" and
 * the vacancy form had never heard of it.
 *
 * The ISO entries are spelled out here even though the rule below matches any
 * number — a suggestion list has to name things, and these are the ones that
 * come up.
 */
export const CERTIFICATION_SUGGESTIONS: string[] = [
    ...CERTIFICATIONS.map(entry => entry.label),
    ...["9001", "14001", "14064", "14067", "14046", "20121", "26000", "45001", "50001"]
        .map(number => `ISO ${number}`)
].sort((a, b) => a.localeCompare(b, "es"));

export const SKILL_SUGGESTIONS: string[] = SKILLS
    .map(entry => entry.label)
    .sort((a, b) => a.localeCompare(b, "es"));
